import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { formatUnixDate } from "core/model/utils/dates";
import { QueryProgress, ValueOf } from "core/types";
import { Banner, Button, Checkbox } from "ds/ui";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import {
  SAVE_AND_CREATE_PDF_ID,
  SAVE_AND_OVERWRITE_PDF_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import {
  CircleCheckBigIcon,
  CircleQuestionMarkIcon,
  FolderInputIcon,
  PrinterIcon,
  TrashIcon,
} from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import { useLocale, useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import { DOCS_FORM_FOOTER_SCREENS, DocsFormFooterScreen } from ".";

export const FOOTER_BANNERS = {
  DELETE_SUCCESS: "delete-success",
  OVERWRITE_SUCCESS: "overwrite-success",
  SAVE_SUCCESS: "save-success",
  SEND_TO_HIS_SUCCESS: "send-to-his-success",
} as const;

export type FooterBannerType = ValueOf<typeof FOOTER_BANNERS>;

const FooterBanner = ({
  banner,
  canPrint,
  onClose,
}: {
  banner: FooterBannerType | null;
  canPrint: boolean;
  onClose: () => void;
}) => {
  const translations = useTranslations();
  switch (banner) {
    case FOOTER_BANNERS.DELETE_SUCCESS:
      return (
        <Banner
          color="success"
          message={
            translations.extractDocs.splitView.footer
              .titleBannerDeletedSuccessfully
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.OVERWRITE_SUCCESS:
      return (
        <Banner
          color="success"
          heading={
            canPrint
              ? translations.extractDocs.splitView.footer
                  .titleBannerOverwrittenSuccessfully
              : undefined
          }
          message={
            canPrint
              ? translations.extractDocs.splitView.footer
                  .descriptionBannerOverwrittenSuccessfully
              : translations.extractDocs.splitView.footer.descriptionSavedOnly
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SAVE_SUCCESS:
      return (
        <Banner
          color="success"
          heading={
            canPrint
              ? translations.extractDocs.splitView.footer
                  .titleBannerSavedSuccessfully
              : undefined
          }
          message={
            canPrint
              ? translations.extractDocs.splitView.footer
                  .descriptionBannerSavedSuccessfully
              : translations.extractDocs.splitView.footer.descriptionSavedOnly
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SEND_TO_HIS_SUCCESS:
      return (
        <Banner
          color="success"
          message={translations.extractDocs.splitView.footer.sendToKisSuccess}
          withClose
          onClose={onClose}
        />
      );
    default:
      return null;
  }
};

export const FormFooter = ({
  canPrint,
  canSendToHis,
  footerBanner,
  footerScreen,
  handleOpenPdf,
  handleSendToHis,
  hasPreviousFormValue,
  isConfirmationChecked,
  onBack,
  onDelete,
  onDeleteProgress,
  onSubmitProgress,
  progressOpenPdf,
  progressSendToHis,
  setConfirmationChecked,
  setFooterBanner,
  setFooterScreen,
  updatedAt,
}: {
  canPrint: boolean;
  canSendToHis: boolean;
  footerBanner: FooterBannerType | null;
  footerScreen: DocsFormFooterScreen;
  hasPreviousFormValue: boolean;
  isConfirmationChecked: boolean;
  onBack: () => void;
  onSubmitProgress: QueryProgress;
  setConfirmationChecked: Dispatch<SetStateAction<boolean>>;
  onDelete: () => Promise<void>;
  onDeleteProgress: QueryProgress;
  setFooterBanner: (banner: FooterBannerType | null) => void;
  setFooterScreen: Dispatch<SetStateAction<DocsFormFooterScreen>>;
  updatedAt: number | undefined;
  handleOpenPdf: () => void;
  handleSendToHis: () => void;
  progressOpenPdf: QueryProgress;
  progressSendToHis: QueryProgress;
}) => {
  const translations = useTranslations();
  const { formState } = useFormContext();
  const locale = useLocale();
  const { hasTwoWaySync } = useHL7ImportStatusContext();

  const FormDetails = () => {
    if (!updatedAt) return null;
    return (
      <p className="gap-2 text-body-md">
        {translations.extractDocs.splitView.footer.lastSavedTimestamp}
        <span className="font-bold">
          {formatUnixDate(updatedAt, locale, { withTime: true })}
        </span>
      </p>
    );
  };

  switch (footerScreen) {
    case DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl"
        >
          <div className="flex gap-4">
            <PrinterIcon className="text-primary" />
            <p className="text-heading-md font-bold">
              {translations.extractDocs.splitView.footer.buttonPrint}
            </p>
          </div>
          <Banner
            color="warning"
            heading={
              translations.extractDocs.splitView.footer.titleWarningNotSaved
            }
            message={
              <div className="flex flex-col gap-4">
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningNotSaved
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningPrint
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
              </div>
            }
          />
          <FormDetails />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-warning-open-pdf-screen"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<PrinterIcon />}
              id="open-pdf"
              isLoading={isLoading(progressOpenPdf)}
              variant="solid"
              onPress={handleOpenPdf}
            >
              {translations.extractDocs.splitView.footer.buttonPrint}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl"
        >
          <div className="flex gap-4">
            <FolderInputIcon className="text-primary" />
            <p className="text-heading-md font-bold">
              {translations.extractDocs.splitView.footer.buttonSendToKis}
            </p>
          </div>
          <Banner
            color="warning"
            heading={
              translations.extractDocs.splitView.footer.titleWarningNotSaved
            }
            message={
              <div className="flex flex-col gap-4">
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningNotSaved
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningKis
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
              </div>
            }
          />
          <FormDetails />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-warning-open-pdf-screen"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<FolderInputIcon />}
              id="send-to-his"
              isLoading={isLoading(progressSendToHis)}
              variant="solid"
              onPress={handleSendToHis}
            >
              {translations.extractDocs.splitView.footer.buttonSendToKis}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl"
        >
          <div className="flex gap-4">
            {hasPreviousFormValue ? (
              <CircleQuestionMarkIcon className="text-primary" />
            ) : (
              <CircleCheckBigIcon className="text-primary" />
            )}
            <div className="flex w-full flex-col gap-4 pt-0.5">
              <p className="text-heading-md font-bold">
                {hasPreviousFormValue
                  ? translations.extractDocs.splitView.footer
                      .titleConfirmationOverwrite
                  : translations.extractDocs.splitView.footer.titleConfirmation}
              </p>
              <p className="text-body-lg">
                {hasPreviousFormValue && canPrint
                  ? translations.extractDocs.splitView.footer
                      .descriptionConfirmationOverwrite
                  : translations.extractDocs.splitView.footer
                      .descriptionConfirmation}
              </p>
            </div>
          </div>
          <Checkbox
            className="gap-2 font-bold"
            isRequired
            label={
              translations.extractDocs.splitView.footer.checkboxConfirmation
            }
            name="confirmation"
            size="md"
            onValueChange={setConfirmationChecked}
          />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-confirmation-screen"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<CircleCheckBigIcon />}
              id={
                hasPreviousFormValue
                  ? SAVE_AND_OVERWRITE_PDF_ID
                  : SAVE_AND_CREATE_PDF_ID
              }
              isDisabled={!isConfirmationChecked}
              isLoading={isLoading(onSubmitProgress)}
              type="submit"
            >
              {
                translations.extractDocs.splitView.footer
                  .buttonSaveAndGeneratePdf
              }
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.DELETE_CONFIRMATION: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.DELETE_CONFIRMATION}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl"
        >
          <div className="flex gap-4">
            <CircleQuestionMarkIcon className="text-primary" />
            <div className="flex w-full flex-col gap-2 pt-0.5">
              <p className="text-heading-md font-bold">
                {canPrint
                  ? translations.extractDocs.splitView.footer
                      .titleDeleteConfirmation
                  : translations.extractDocs.splitView.footer
                      .titleDeleteConfirmationOnlyContent}
              </p>
              <p className="text-body-lg">
                {
                  translations.extractDocs.splitView.footer
                    .descriptionDeleteConfirmation
                }
              </p>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="black"
              id="close-delete-confirmation-screen"
              variant="light"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="danger"
              endContent={<TrashIcon />}
              id="delete-form"
              isLoading={isLoading(onDeleteProgress)}
              onPress={onDelete}
            >
              {canPrint
                ? translations.extractDocs.splitView.footer
                    .buttonDeleteContentAndPdf
                : translations.extractDocs.splitView.footer
                    .buttonDeleteOnlyContent}
            </Button>
          </div>
        </div>
      );
    }
    default:
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.DEFAULT}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-4 border-t border-gray-400 bg-white p-4 shadow-2xl"
        >
          <FooterBanner
            banner={footerBanner}
            canPrint={canPrint}
            onClose={() => setFooterBanner(null)}
          />
          <FormDetails />
          <div
            className={clsx("flex flex-wrap items-center justify-end gap-4", {
              "justify-between": hasPreviousFormValue,
            })}
          >
            {hasPreviousFormValue && (
              <div className="flex flex-wrap gap-4">
                <Button
                  color="danger"
                  id="delete-form"
                  startContent={<TrashIcon />}
                  variant="light"
                  onPress={() => {
                    setFooterScreen(
                      DOCS_FORM_FOOTER_SCREENS.DELETE_CONFIRMATION,
                    );
                  }}
                >
                  {translations.patientForms.menu.buttonDelete}
                </Button>
                {canPrint ? (
                  <Button
                    color="primary"
                    id="open-pdf"
                    isLoading={isLoading(progressOpenPdf)}
                    startContent={<PrinterIcon />}
                    variant="ghost"
                    onPress={handleOpenPdf}
                  >
                    {translations.extractDocs.splitView.footer.buttonPrint}
                  </Button>
                ) : null}
                {hasTwoWaySync && canSendToHis ? (
                  <Button
                    color="primary"
                    id="send-to-his"
                    isLoading={isLoading(progressSendToHis)}
                    startContent={<FolderInputIcon />}
                    variant="ghost"
                    onPress={handleSendToHis}
                  >
                    {translations.extractDocs.splitView.footer.buttonSendToKis}
                  </Button>
                ) : null}
              </div>
            )}
            <Button
              color="primary"
              endContent={<CircleCheckBigIcon />}
              id="review-and-save-pdf"
              isDisabled={!formState.isDirty}
              type="submit"
            >
              {translations.extractDocs.splitView.footer.buttonReviewAndSave}
            </Button>
          </div>
        </div>
      );
  }
};
